import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import '../style.css';
import '../nov.css';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Debug log for state changes
  useEffect(() => {
    console.log('ChatBot isOpen state changed:', isOpen);
  }, [isOpen]);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I am Novi, your MediaNov assistant. I can answer your questions about music distribution, copyright, publishing services and MediaNov. How can I help you?",
      isBot: true,
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const API_KEY = 'AIzaSyCtyA3TJIO0Z9guIfDb5EvM5iN10efS7W8';
  const genAI = new GoogleGenerativeAI(API_KEY);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const SYSTEM_PROMPT = `
You are MediaNov's official customer service assistant. You can only help with the following topics:

**About MediaNov:**
- MediaNov is a company that provides professional music distribution and publishing services
- Website: medianov.com
- Contact: contact@medianov.com
- We offer comprehensive services to artists in the music industry

**Our Services:**
1. **Music Distribution**: Music distribution on all major platforms like Spotify, Apple Music, YouTube Music, Amazon Music
2. **Copyright Management**: Protection and management of music copyrights
3. **Music Publishing**: Professional music publishing services
4. **Royalty Collection**: Tracking and collection of music revenues
5. **Licensing and Sync**: Music licensing and synchronization services
6. **Analytics and Insights**: Music performance analytics
7. **Artist Services**: Specialized support services for artists

**Our Partners:**
- The Orchard
- Sony Music
- Puntilla
- Keep Hold

**Our Labels:**
- Media Nov Inc. (FOLK music focused)
- Media Nov Music (POP music focused)
- Media Nov Records (DANCE music focused)

**Contact Forms and Complaint Channels:**
- **General Contact**: You can contact us through the /contact page
- **Copyright Violation Complaint**: You can report copyright violations through the /copyright-report page
- **Artist Services**: You can get information about artist services through the /artist-services page
- **Music Publishing**: You can apply for publishing services through the /music-publishing page
- **Resources**: You can explore useful resources through the /resources page

**Important Notes:**
- You can only help with MediaNov, music distribution, copyright, and music industry topics
- If asked about contact forms and complaint channels, share the links above
- If asked about other topics, mention that you can only provide information about MediaNov services
- Respond in English
- Give short and clear answers
- Be professional like customer service
- If detailed information is needed, direct to contact@medianov.com
`;

  const sendMessage = async () => {
    if (!inputText.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputText,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });
      const prompt = SYSTEM_PROMPT + `\n\nUser question: ${inputText}`;
      
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const botResponse = response.text();

      const botMessage = {
        id: Date.now() + 1,
        text: botResponse,
        isBot: true,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: 'Sorry, I am experiencing a technical issue right now. Please try again later or contact us at contact@medianov.com.',
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('tr-TR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <div 
        className="fixed bottom-6 right-6" 
        style={{
          zIndex: 9999,
          position: 'fixed',
          bottom: '24px',
          right: '24px'
        }}
      >
        <button
          onClick={() => {
            console.log('Chat button clicked! Current isOpen:', isOpen);
            console.log('Setting isOpen to:', !isOpen);
            setIsOpen(!isOpen);
          }}
          className="bg-gradient-to-r from-[#9BCFFF] via-[#FCD4FE] to-[#B6F1F6] text-black p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
          aria-label="Chat with MediaNov Assistant"
          style={{
            cursor: 'pointer',
            width: '60px',
            height: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            outline: 'none'
          }}
        >
          {isOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          )}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: '96px',
          right: '24px',
          width: '384px',
          height: '500px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
          zIndex: 9998,
          animation: 'fade-in-up 1s ease-out forwards'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(to right, #9BCFFF, #FCD4FE, #B6F1F6)',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <div style={{
                  width: '88px',
                  height: '88px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                <img src="/novi-logo.gif" alt="Novi" style={{width: '80px', height: '80px', objectFit: 'contain'}} />
              </div>
              <div>
                <h3 style={{fontWeight: '600', color: 'black', margin: 0}}>Novi</h3>
                <p style={{fontSize: '12px', color: '#374151', margin: 0}}>Music distribution expert</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                color: 'black',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px'
              }}
              onMouseEnter={(e) => e.target.style.color = '#374151'}
              onMouseLeave={(e) => e.target.style.color = 'black'}
            >
              <svg style={{width: '20px', height: '20px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div 
            ref={chatContainerRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#f9fafb',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.isBot ? 'flex-start' : 'flex-end'
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '12px',
                    borderRadius: '16px',
                    background: message.isBot 
                      ? 'white'
                      : 'linear-gradient(to right, #9BCFFF, #FCD4FE, #B6F1F6)',
                    color: 'black',
                    border: message.isBot ? '1px solid #e5e7eb' : 'none',
                    wordWrap: 'break-word'
                  }}
                >
                  <p style={{fontSize: '14px', whiteSpace: 'pre-wrap', margin: 0}}>{message.text}</p>
                  <p style={{fontSize: '12px', opacity: 0.7, marginTop: '4px', margin: '4px 0 0 0'}}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div style={{display: 'flex', justifyContent: 'flex-start'}}>
                <div style={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid #e5e7eb',
                  padding: '12px',
                  borderRadius: '16px'
                }}>
                  <div style={{display: 'flex', gap: '4px'}}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#9ca3af',
                      borderRadius: '50%',
                      animation: 'bounce 1s infinite'
                    }}></div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#9ca3af',
                      borderRadius: '50%',
                      animation: 'bounce 1s infinite',
                      animationDelay: '0.1s'
                    }}></div>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#9ca3af',
                      borderRadius: '50%',
                      animation: 'bounce 1s infinite',
                      animationDelay: '0.2s'
                    }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '16px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: 'white'
          }}>
            <div style={{display: 'flex', gap: '8px'}}>
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask questions about MediaNov..."
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '12px',
                  outline: 'none',
                  fontSize: '14px'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputText.trim()}
                style={{
                  background: 'linear-gradient(to right, #9BCFFF, #FCD4FE, #B6F1F6)',
                  color: 'black',
                  padding: '12px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: isLoading || !inputText.trim() ? 'not-allowed' : 'pointer',
                  opacity: isLoading || !inputText.trim() ? 0.5 : 1,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && inputText.trim()) {
                    e.target.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.15)';
                    e.target.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <svg style={{width: '20px', height: '20px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
